import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { IIshikawaCategory, NotificationAction, NotificationReducer } from 'types/notification'
import NotificationActions from 'actions/Notification'
import { Dispatch } from 'redux'
import { Beforeunload } from 'react-beforeunload'
import {
    IshikawaContainer,
    IshikawaMain,
    IshikawaContent,
    SaveButton,
    TitleIshikawaForm,
    Top,
    TopInputs,
    BackButton,
    Subtitle,
    inputRequiredStyle
} from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/IshikawaDiagram/styles'
import { InputLabel } from 'components/InputLabel'
import { Label } from 'components/InputLabel/styles'
import { FieldErrors, FieldValues, UseFormGetValues, UseFormRegister, UseFormSetValue, useForm } from 'react-hook-form'
import LeftSideV1 from 'pages/Notifications/LeftSideV1'
import { Confirm } from 'components/Modals/Confirm'
import { SubTitleText, TitleText } from 'pages/NotifyEvent/styles'
import { IconWarning } from 'components/NavBar/svg'
import NotificationService from 'services/Notification'
import NumberOrBulletTextArea from 'components/NumberOrBulletTextArea'
import { useCallbackPrompt } from 'hooks/useCallbackPrompt'

interface FieldType {
    name: string;
    label: string;
    subLabel: string;

}
interface FieldTypeProp extends FieldType {
    setValue: UseFormSetValue<FieldValues>
    register: UseFormRegister<FieldValues>
    getValues: UseFormGetValues<FieldValues>
    errors: FieldErrors<FieldValues>
    onChangeHelper: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
}
/**
 * Formulário principal para edição das informações do diagrama de ishikawa
 */
const FormIshikawa: React.FC = () => {
    //native
    const esc = encodeURIComponent
    
    const { id } = useParams()
    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()

    const [notificationsList, setNotificationsList] = useState<any>([])
    const [isDirty, setIsDirty] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState(false)

    const MSG_SUCCESS = `Análise de Ishikawa realizada com sucesso!`

    const STATE_FIELDS = {
        method: { title: 'MÉTODO' },
        measurement: { title: 'MEDIDA' },
        material: { title: 'MATERIAL' },
        environment: { title: 'MEIO AMBIENTE' },
        machine: { title: 'MÁQUINA' },
        manpower: { title: 'MÃO DE OBRA' }
    }
    const fields: FieldType[] = [
        {
            name: 'MÉTODO',
            label: 'MÉTODO',
            subLabel: 'Qualquer fator relacionado com procedimento.',
        },
        {
            name: 'MEDIDA',
            label: 'MEDIDA',
            subLabel: 'Qualquer fator relacionado com o monitoramento ou as métricas de controle do processo.',
        },
        {
            name: 'MÃO DE OBRA',
            label: 'MÃO DE OBRA',
            subLabel: 'Qualquer fator relacionado a falha humana ou pessoas.',
        },
        {
            name: 'MEIO AMBIENTE',
            label: 'MEIO AMBIENTE',
            subLabel: 'Qualquer fator relacionado com infra-estrutura e ambientes externos e internos.',
        },
        {
            name: 'MATERIAL',
            label: 'MATERIAL',
            subLabel: 'Qualquer fator relacionado a itens, componentes, insumos ou matérias-primas.',
        },
        {
            name: 'MÁQUINA',
            label: 'MÁQUINA',
            subLabel: 'Qualquer fator relacionado a equipamentos.',
        },
    ]

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues
    } = useForm()
    const navigate = useNavigate()


    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )
    useEffect(() => {
        //NotificationActions.getIshikawaByNotificationId(notificationDispatch, id)
        if (!notificationSelector.notificationById) {
            NotificationActions.getNotificationById(notificationDispatch, id)
        }
        const ishikawa = notificationSelector.ishikawaDiagram;
        if(ishikawa){
            let categories = [...ishikawa.categories]
            redrawAllCauses(categories)
        }
    }, [])


    function redrawAllCauses(categories: IIshikawaCategory[]) {
        for (let cat of categories) {
            for (let field in STATE_FIELDS) {
                let lis = []
                if (STATE_FIELDS.hasOwnProperty(field) && esc(cat.title.trim()) === esc(STATE_FIELDS[field].title)) {
                    if (cat.causes.length > 0) {
                        for (let cause of cat.causes) {
                            if(cause.length > 0){
                                const text = `• ${cause.trim()}\n`
                                lis.push(text)
                            }
                        }
                        setValue(STATE_FIELDS[field].title, lis.join(''))
                    }
                }
            }
        }

        return categories
    }

    const handleSubmitForm = () => {
        setIsSaving(true)

        const ishikawaObjSn = {
            hospital_incident: id,
            sys_id: notificationSelector.ishikawaDiagram?.sys_id,
            material: buildStringFieldRequest(getValues(STATE_FIELDS.material.title)),
            environment: buildStringFieldRequest(getValues(STATE_FIELDS.environment.title)),
            machine: buildStringFieldRequest(getValues(STATE_FIELDS.machine.title)),
            manpower: buildStringFieldRequest(getValues(STATE_FIELDS.manpower.title)),
            measurement: buildStringFieldRequest(getValues(STATE_FIELDS.measurement.title)),
            method: buildStringFieldRequest(getValues(STATE_FIELDS.method.title)),
            description: getValues('description') || notificationSelector.notificationById?.u_reclassified_incident_type?.display_value || ''
        }
        NotificationService.saveIshikawaDiagram(ishikawaObjSn).then((response: any) => {
            navigate(`/notificacoes/${id}`, { state: { currentStep: 2, messageSuccess: MSG_SUCCESS } })
        }).catch((error) => {
            console.log(error)
        }).finally(() => {
            setIsSaving(false)
            navigate(`/notificacoes/${id}`, { state: { currentStep: 2, messageSuccess: MSG_SUCCESS } })
        })

    }

    const [showPrompt, confirmNavigation, cancelNavigation] =
        useCallbackPrompt(isDirty && !isSaving)



    /**
     * 
     * @param categories 
     * @param titleCategory 
     * @returns 
     */
    const buildStringFieldRequest = (categories: string) => {
        const regex = /• \s*/g;
        categories = categories.replace(/• \s*/, '');
        const clean = categories.replace(regex, ' |')
        return clean
    }

    /**
     * Remove o diagrama atual da sessão e redireciona para o step 2 (Editar diagrama)
     */
    const clearAndRedirectIshikawaDiagram = () => {
        navigate(`/notificacoes/${id}`, { state: { currentStep: 2, messageSuccess: null } })
    }

    // Evento disparado a partir do botão 'Voltar'
    const backIshikawaDiagram = useCallback((e: any) => {
        if (!isDirty || hasWriting()) {
            clearAndRedirectIshikawaDiagram()
        }
    }, [])




    /**
     * Evento usado quando são digitados itens dinamiamente em cada listagem
     * Preenche cada um desse itens em um objeto e armazena na sessão
     */
    const onChangeHelper = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setIsDirty(hasWriting())
    }
    // Componente que renderiza cada caixa de texto editável em tela.

    //verifica se existe ao menos um campo com registro 
    const hasWriting = () =>{
        const regex = /• \s*/g;
        let names= ['MÉTODO', 'MEDIDA', 'MÃO DE OBRA', 'MEIO AMBIENTE', 'MATERIAL', 'MÁQUINA']
        
        let existContent  = names.map(item => {
            let fieldName = getValues(item);
            let len = fieldName?.length;
            if(len > 0){
                let content = fieldName?.replace(regex, '').trim();
                return content.length > 0
            }   
            return false;
        })
       
        return existContent.filter(i => i).length > 0
    }

    return (
        <>
            {isDirty && (
                <Beforeunload onBeforeunload={(event) => 'Você fez edições que não forma salvas.'} />
            )}
            <Confirm
                isOpen={showPrompt as boolean}
                handleConfirmModal={confirmNavigation as any}
                handleCloseModal={cancelNavigation as any}
                labelButtomClose={`Sair`}
                labelButtomConfirm={`Continuar edição`}
            >
                <div className='justify-center flex p-1'>
                    <IconWarning></IconWarning>
                </div>
                <TitleText className={'text-yellow text-xl font-semibold justify-center flex'}>
                    Edição não salva
                </TitleText>
                <div className='justify-center flex'>
                    <SubTitleText >
                        <p>Você fez edições que não foram salvas.</p>
                        <p>Se sair, você perderá as edições.</p>
                    </SubTitleText>
                </div>
            </Confirm>
            <IshikawaContainer data-testid="test-ishikawa-form">
                <IshikawaMain onSubmit={handleSubmit(handleSubmitForm)}>
                    <Top>
                        <TitleIshikawaForm data-testid='notifications'>
                            Fazer a Análise do Diagrama de Ishikawa
                        </TitleIshikawaForm>
                        
                        <TopInputs>
                            <BackButton type='button' onClick={backIshikawaDiagram}>Voltar</BackButton>
                            <SaveButton type='submit' className={`${!isDirty ? 'bg-[#E2E2E2]' : ''}`} disabled={!isDirty} >Salvar</SaveButton>
                        </TopInputs>

                        <Subtitle>
                            Identifique os principais fatores que levaram o
                            incidente acontecer, e preeencha as causas relacionadas
                            as classes do diagrama:
                        </Subtitle>
                    </Top>
                    <IshikawaContent>
                        <div className='flex flex-col col-span-2'>
                            <div className='container'>
                                <div className='w-full px-8 mt-6 mb-10'>
                                    <div>
                                        <span>
                                            <Label className='p-0 text-xs font-semibold text-darkGray'>
                                                EFEITO INESPERADO
                                            </Label>
                                        </span>

                                        <InputLabel
                                            defaultValue={notificationSelector.notificationById?.u_reclassified_incident_type?.display_value}
                                            name='description'
                                            register={register}
                                            className={`max-w-full`}
                                            placeholder='Digite aqui'
                                            inputLength={80}
                                            maxLength={80}
                                            readOnly={true}
                                            error={{
                                                message: ''
                                            }}
                                        />
                                    </div>
                                    <div
                                        data-testid="test-text-area-container"
                                        className='flex flex-wrap gap-4'>
                                        {
                                            fields.map((field, idx) => {
                                                return (
                                                    <div key={idx}>
                                                        <TextEditableList
                                                            name={field.name}
                                                            label={field.label}
                                                            subLabel={field.subLabel}
                                                            setValue={setValue}
                                                            register={register}
                                                            getValues={getValues}
                                                            errors={errors}
                                                            onChangeHelper={onChangeHelper}
                                                        />
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <LeftSideV1
                            isShowAnalyze={false}
                            currentNotificationSelected={
                                notificationSelector.notificationById
                            }
                            notificationsList={notificationsList}
                            setNotificationsList={setNotificationsList}
                        />
                    </IshikawaContent>
                </IshikawaMain>
            </IshikawaContainer>
        </>
    )
}
const TextEditableList = ({ label, subLabel, name, register, setValue, getValues, errors, onChangeHelper }: FieldTypeProp) => {
    return (
        <>
            <div className='flex flex-col gap-4 sm:w-[365px]  xl:min-w-[365px] 2xl:min-w-[400px] mt-[34px]'>
                <span className='h-[35px]'>
                    <Label className='p-0 text-xs font-semibold text-darkGray'>{label}</Label>
                    <Label className='text-xs text-darkGray/80 font-normal whitespace-normal'>
                        {subLabel}
                    </Label>
                </span>

                <div className={`flex items-center relative`}>
                    <NumberOrBulletTextArea
                        maxLength={1000}
                        isDotOrNumber="dot"
                        setValue={setValue}
                        getValues={getValues}
                        name={name}
                        register={register}
                        textAreaStyle={`sm:w-[365px] xl:min-w-[365px] xxl:min 2xl:min-w-[400px] h-[260px] focus:border-1 focus:border-black ${errors[name] && inputRequiredStyle}`}
                        placeholder="&#8226; Digite aqui"
                        rows={8}
                        onChangeHelper={onChangeHelper}
                    />
                </div>
            </div>
        </>
    )
}
export { FormIshikawa }
